import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Box from '@mui/material/Box';
import './CryptoForm.css';
import Header from './header';
import WhatIf from './WhatIf';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Footer from './footer';
import Heatmap from './heatmap';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import Rocket from './rocket.svg'; // Import the rocket SVG
import RefreshPortfolioTable from './RefreshPortfolioTable'; // Import the RefreshPortfolioTable component
import LandingPage from './LandingPage'; // Import the LandingPage component
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CircleIcon from '@mui/icons-material/Circle'; // Import CircleIcon for risk rating
// import { fetchCoinsList, createSymbolToIdMap } from './api';
import { syncPortfolioToLocalStorage } from './cacheUtils';

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const RATE_LIMIT_DELAY = 2000; // 2 seconds delay to stay within 30 requests per minute

const generateRowKey = (item) => {
  // Combine token name, amount, and price to create a unique key
  return `${item.token}_${item.amount}_${item.price}`;
};

const fetchWithCache = async (url) => {
  const cacheKey = `cache_${url}`;
  const cachedData = localStorage.getItem(cacheKey);
  const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);

  // Check cache validity (e.g., 24 hours)
  if (cachedData && cacheTimestamp && new Date().getTime() - cacheTimestamp < CACHE_DURATION) {
    return JSON.parse(cachedData);
  }

  // Fetch data if not in cache
  try {
    const data = await fetchData(url);
    if (data) {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime());
    }
    return data;
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    return null; // Return null to indicate failure
  }
};

const fetchData = async (url, retries = 3) => {
  try {
    const response = await fetchWithRateLimit(url);
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    if (retries > 0) {
      console.warn(`Retrying fetch for ${url}. Attempts left: ${retries}`);
      return fetchData(url, retries - 1);
    } else {
      console.error(`Failed to fetch data from ${url}:`, error);
      throw error; // Propagate error for further handling
    }
  }
};

const fetchWithRateLimit = (() => {
  let queue = [];
  let isProcessing = false;

  const processQueue = async () => {
    if (isProcessing || queue.length === 0) return;
    isProcessing = true;

    while (queue.length > 0) {
      const { url, resolve, reject } = queue.shift();
      try {
        const response = await fetch(url);
        resolve(response);
      } catch (error) {
        reject(error);
      }
      await new Promise((r) => setTimeout(r, RATE_LIMIT_DELAY)); // Delay between requests
    }
    isProcessing = false;
  };

  return (url) =>
    new Promise((resolve, reject) => {
      queue.push({ url, resolve, reject });
      processQueue();
    });
})();

const formatPrice = (price) => {
  if (isNaN(price)) return 'N/A';
  const options = {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  return new Intl.NumberFormat('en-US', options).format(price);
};

const formatDetailedPrice = (price) => {
  if (isNaN(price)) return 'N/A';
  const options = {
    style: 'decimal',
    minimumFractionDigits: price < 0.99 ? 8 : 2,
    maximumFractionDigits: 10,
  };
  return new Intl.NumberFormat('en-US', options).format(price);
};

const calculateBreakevenMultiplier = (price, currentPrice) => {
  if (currentPrice <= 0) {
    return { multiplier: "N/A", message: "Invalid current price" };
  }

  const multiplier = (price / currentPrice).toFixed(2);
  let message;

  if (multiplier < 1) {
    const profitPercentage = (((currentPrice - price) / price) * 100).toFixed(2);
    message = `In Profit: ${profitPercentage}% gain`;
  } else if (multiplier >= 1 && multiplier <= 1.2) {
    message = "Near Breakeven: Almost recovered";
  } else if (multiplier > 1.2 && multiplier <= 2) {
    message = "Moderate Recovery Needed";
  } else {
    message = `High Recovery Needed: ${multiplier}x required to breakeven`;
  }

  return { multiplier, message };
};

const calculateRisk = (volatility, drawdown, correlation, liquidity, marketCap) => {
  // Example risk calculation logic
  const riskScore = (volatility + drawdown + correlation + liquidity + marketCap) / 5;
  if (riskScore < 2) return 'Low';
  if (riskScore < 4) return 'Medium';
  return 'High';
};

const getRiskColor = (risk) => {
  switch (risk) {
    case 'Low':
      return 'green';
    case 'Medium':
      return 'orange';
    case 'High':
      return 'red';
    default:
      return 'gray';
  }
};

const calculateAdditionalMetrics = (item, btcMarketCap, ethMarketCap) => {
  const closeToAth = ((item.current_price / item.ath_price) * 100).toFixed(2);
  const athProfit = (parseFloat(item.amount) * parseFloat(item.ath_price)).toFixed(2);
  const fiveXProfit = (parseFloat(item.amount) * parseFloat(item.current_price) * 5).toFixed(2);
  const tenXProfit = (parseFloat(item.amount) * parseFloat(item.current_price) * 10).toFixed(2);
  const twentyXProfit = (parseFloat(item.amount) * parseFloat(item.current_price) * 20).toFixed(2);
  const hundredXProfit = (parseFloat(item.amount) * parseFloat(item.current_price) * 100).toFixed(2);
  const marketCapTier = 
    item.market_cap > 1000000000
      ? "Large Cap (>$1B): Stable and lower risk"
      : item.market_cap > 100000000
      ? "Mid Cap ($100M - $1B): Balanced growth potential"
      : "Small Cap (<$100M): High risk, high reward, volatile";

  const { multiplier: breakevenMultiplier, message: breakevenMessage } = calculateBreakevenMultiplier(item.price, item.current_price);
  const btcComparisonPrice = btcMarketCap ? (btcMarketCap / item.total_supply).toFixed(6) : 'N/A';
  const ethComparisonPrice = ethMarketCap ? (ethMarketCap / item.total_supply).toFixed(6) : 'N/A';

  // Prevent division by zero
const profitPercentage = item.price > 0 
? ((item.current_price - item.price) / item.price) * 100 
: 0;

// Define key thresholds
const isSell = profitPercentage > 100;
const isProfit = profitPercentage > 0;
const isLoss = item.current_price < item.price * 0.85;
const isHold = Math.abs(profitPercentage) <= 15;

// Determine action based on thresholds
const action = isSell 
? 'Sell' 
: isProfit 
  ? 'Profit' 
  : isLoss 
    ? 'Loss' 
    : 'Hold :)';

// Determine action color
const actionColor = isSell 
? 'gold' // Sell (gold for strong emphasis)
: isProfit 
  ? 'green' // Profit
  : isLoss 
    ? 'red' // Loss
    : 'blue'; // Hold :)

  return {
    closeToAth,
    athProfit,
    fiveXProfit,
    tenXProfit,
    twentyXProfit,
    hundredXProfit,
    marketCapTier,
    breakevenMultiplier,
    breakevenMessage,
    btcComparisonPrice,
    ethComparisonPrice,
    riskRating: calculateRisk(item.volatility, item.drawdown, item.correlation, item.liquidity, item.market_cap), // Include risk rating
    action, // Add action field
    actionColor, // Add action color field
  };
};

const CryptoForm = () => {
  const [token, setToken] = useState('');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [portfolio, setPortfolio] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editTokenName, setEditTokenName] = useState('');
  const [isWhatIfOpen, setIsWhatIfOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [editTokenThumb, setEditTokenThumb] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [symbolToIdMap, setSymbolToIdMap] = useState({});
  const [btcMarketCap, setBtcMarketCap] = useState(null);
  const [ethMarketCap, setEthMarketCap] = useState(null);
  const [visibleItems, setVisibleItems] = useState(10); // State to track visible items
  const [hideNumbers, setHideNumbers] = useState(false); // State to track hiding numbers
  const [view, setView] = useState('treemap'); // State to toggle between treemap and pie chart

  useEffect(() => {
    const storedPortfolio = JSON.parse(localStorage.getItem('portfolio'));
    if (storedPortfolio) {
      setPortfolio(Array.isArray(storedPortfolio) ? storedPortfolio : []);
    }

    const storedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(storedDarkMode);
    if (storedDarkMode) {
      document.body.classList.add('dark-mode');
    }

    const fetchAndSetCoinsList = async () => {
      const cachedList = localStorage.getItem('coinsList');
      const cacheTimestamp = localStorage.getItem('coinsListTimestamp');
  
      // Check cache validity (e.g., 7 days)
      if (cachedList && cacheTimestamp && new Date().getTime() - cacheTimestamp < 7 * 24 * 60 * 60 * 1000) {
        setSymbolToIdMap(JSON.parse(cachedList));
      } else {
        try {
          // const coinsList = await fetchCoinsList(); // Fetch from CoinGecko
          // const map = createSymbolToIdMap(coinsList); // Create a symbol-to-ID map
          // setSymbolToIdMap(map);
          // localStorage.setItem('coinsList', JSON.stringify(map)); // Cache the list
          localStorage.setItem('coinsListTimestamp', new Date().getTime());
        } catch (error) {
          console.error('Failed to fetch token list:', error);
        }
      } 
    };

    fetchAndSetCoinsList();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshPrices();
    }, 24 * 60 * 60 * 1000); // 24 hours

    return () => clearInterval(interval);
  }, [portfolio]);

  useEffect(() => {
    const fetchMarketCaps = async () => {
      try {
        const btcData = await fetchWithCache('https://api.coingecko.com/api/v3/coins/bitcoin');
        const ethData = await fetchWithCache('https://api.coingecko.com/api/v3/coins/ethereum');
        setBtcMarketCap(btcData.market_data.market_cap.usd);
        setEthMarketCap(ethData.market_data.market_cap.usd);
      } catch (err) {
        console.error('Failed to fetch market caps:', err);
      }
    };
    fetchMarketCaps();
  }, []);

  const fetchSearchResults = useCallback(
    debounce(async (query) => {
      if (!query.trim()) return setSearchResults([]);

      setLoading(true);
      try {
        const data = await fetchWithCache(`https://api.coingecko.com/api/v3/search?query=${query}`);
        if (data && data.coins) {
          const results = data.coins.slice(0, 5); // Limit results to top 5
          setSearchResults(results);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        setError('Error fetching search results. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 300), // Reduce debounce delay to 300ms
    []
  );

  useEffect(() => {
    if (token) {
      fetchSearchResults(token);
    } else {
      setSearchResults([]);
    }
  }, [token, fetchSearchResults]);

  const handleSelect = (result) => {
    setSelectedToken({
      id: result.id,
      name: result.name,
      symbol: result.symbol,
      thumb: result.thumb || '',
    });
    setToken('');
    setSearchResults([]);
    document.getElementById('add-token').style.display = 'none'; // Hide the search bar
  };

  const handleAddFromForm = async () => {
    if (!selectedToken || !amount || !price) {
      setError('Please select a token and enter valid amount and price.');
      return;
    }

    if (parseFloat(amount) <= 0 || parseFloat(price) <= 0) {
      setError('Amount and price must be positive numbers.');
      return;
    }

    const existingTokenIndex = portfolio.findIndex(item => item.token === selectedToken.name);

    if (existingTokenIndex !== -1) {
      // Update existing token
      const updatedPortfolio = portfolio.map((item, index) =>
        index === existingTokenIndex
          ? {
              ...item,
              amount: parseFloat(amount),
              price: parseFloat(price).toFixed(10),
              current_price: 'Loading...',
              ath_price: 'Loading...',
              market_cap: 'Loading...',
              total_supply: 'Loading...',
              volatility: item.volatility || 'N/A', // Ensure volatility is included
              drawdown: item.drawdown || 'N/A', // Ensure drawdown is included
              correlation: item.correlation || 'N/A', // Ensure correlation is included
              liquidity: item.liquidity || 'N/A', // Ensure liquidity is included
            }
          : item
      );
      setPortfolio(updatedPortfolio);
      syncPortfolioToLocalStorage(updatedPortfolio); // Save to local storage

      setTimeout(async () => {
        try {
          const data = await fetchWithCache(`https://api.coingecko.com/api/v3/coins/${selectedToken.id}`);
          const finalUpdatedPortfolio = updatedPortfolio.map((item) => {
            if (item.token === selectedToken.name) {
              return {
                ...item,
                current_price: data.market_data.current_price.usd,
                ath_price: data.market_data.ath.usd,
                market_cap: data.market_data.market_cap.usd,
                total_supply: data.market_data.total_supply,
                volatility: data.market_data.volatility || 'N/A', // Update volatility
                drawdown: data.market_data.drawdown || 'N/A', // Update drawdown
                correlation: data.market_data.correlation || 'N/A', // Update correlation
                liquidity: data.market_data.liquidity || 'N/A', // Update liquidity
              };
            }
            return item;
          });
          setPortfolio(finalUpdatedPortfolio);
          syncPortfolioToLocalStorage(finalUpdatedPortfolio); // Save to local storage
        } catch (error) {
          console.error('Failed to fetch token details:', error);
        }
      }, 10000); // 10 seconds delay
    } else {
      // Add new token
      const newPortfolio = [
        ...portfolio,
        {
          token: selectedToken.name,
          symbol: selectedToken.symbol,
          amount: parseFloat(amount),
          price: parseFloat(price).toFixed(10),
          current_price: 'Loading...',
          ath_price: 'Loading...',
          market_cap: 'Loading...',
          total_supply: 'Loading...',
          thumb: selectedToken.thumb,
          volatility: 'N/A', // Initialize volatility
          drawdown: 'N/A', // Initialize drawdown
          correlation: 'N/A', // Initialize correlation
          liquidity: 'N/A', // Initialize liquidity
        },
      ];
      setPortfolio(newPortfolio);
      syncPortfolioToLocalStorage(newPortfolio); // Save to local storage

      setTimeout(async () => {
        try {
          const data = await fetchWithCache(`https://api.coingecko.com/api/v3/coins/${selectedToken.id}`);
          const finalUpdatedPortfolio = newPortfolio.map((item) => {
            if (item.token === selectedToken.name) {
              return {
                ...item,
                current_price: data.market_data.current_price.usd,
                ath_price: data.market_data.ath.usd,
                market_cap: data.market_data.market_cap.usd,
                total_supply: data.market_data.total_supply,
                volatility: data.market_data.volatility || 'N/A', // Update volatility
                drawdown: data.market_data.drawdown || 'N/A', // Update drawdown
                correlation: data.market_data.correlation || 'N/A', // Update correlation
                liquidity: data.market_data.liquidity || 'N/A', // Update liquidity
              };
            }
            return item;
          });
          setPortfolio(finalUpdatedPortfolio);
          syncPortfolioToLocalStorage(finalUpdatedPortfolio); // Save to local storage
        } catch (error) {
          console.error('Failed to fetch token details:', error);
        }
      }, 10000); // 10 seconds delay
    }

    resetAddFormState();
  };

  const resetAddFormState = () => {
    setIsAddModalOpen(false); // Close the modal
    setSelectedToken(null);   // Clear the selected token
    setAmount('');            // Reset the amount field
    setPrice('');             // Reset the price field
    setToken('');             // Clear the token search input
    setSearchResults([]);     // Clear search results
    setError('');             // Clear any error messages
  };

  const handleRemoveSelection = () => {
    setSelectedToken(null);
    document.getElementById('add-token').style.display = 'block'; // Show the search bar
  };

  const handleEdit = (key) => {
    const index = portfolio.findIndex((item) => generateRowKey(item) === key);
    if (index !== -1) {
      setEditIndex(index);
      setEditAmount(portfolio[index].amount);
      setEditPrice(parseFloat(portfolio[index].price).toFixed(10));
      setEditTokenName(portfolio[index].token);
      setEditTokenThumb(portfolio[index].thumb || ''); // Add null check
      setIsModalOpen(true);
    }
  };
  

  const handleUpdate = () => {
    if (editIndex !== null) {
      const updatedPortfolio = portfolio.map((item, i) =>
        i === editIndex
          ? { ...item, amount: editAmount, price: parseFloat(editPrice).toFixed(10) }
          : item
      );
      setPortfolio(updatedPortfolio); // Update the state
      syncPortfolioToLocalStorage(updatedPortfolio); // Save to local storage
      resetEditState(); // Reset the editing state
    }
  };

  const resetEditState = () => {
    setEditIndex(null);
    setEditAmount('');
    setEditPrice('');
    setEditTokenName('');
    setEditTokenThumb(''); // Reset token image
    setIsModalOpen(false);
  };

 const handleDelete = (key) => {
  const index = portfolio.findIndex((item) => generateRowKey(item) === key);
  if (index !== -1) {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  }
};


const confirmDelete = () => {
  if (deleteIndex !== null) {
    const updatedPortfolio = portfolio.filter((_, i) => i !== deleteIndex);
    setPortfolio(updatedPortfolio);
    syncPortfolioToLocalStorage(updatedPortfolio); // Save to local storage
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  }
};


  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  const refreshPrices = async () => {
    try {
      const updatedPortfolio = await Promise.all(portfolio.map(async (item) => {
        const data = await fetchWithCache(`https://api.coingecko.com/api/v3/coins/${item.token.toLowerCase()}`);
        return { ...item, current_price: data.market_data.current_price.usd, ath_price: data.market_data.ath.usd, market_cap: data.market_data.market_cap.usd, total_supply: data.market_data.total_supply, volatility: data.market_data.volatility || 'N/A', drawdown: data.market_data.drawdown || 'N/A', correlation: data.market_data.correlation || 'N/A', liquidity: data.market_data.liquidity || 'N/A' };
      }));
      setPortfolio(updatedPortfolio);
      syncPortfolioToLocalStorage(updatedPortfolio); // Save to local storage
    } catch (err) {
      setError('Failed to refresh prices. Please try again later.');
    }
  };

  const calculateSummary = () => {
    const totalInvestment = portfolio.reduce((acc, item) => acc + parseFloat(item.amount) * parseFloat(item.price), 0).toFixed(2);
    const totalValue = portfolio.reduce((acc, item) => acc + parseFloat(item.amount) * parseFloat(item.current_price), 0).toFixed(2);
    const totalProfitLoss = (totalValue - totalInvestment).toFixed(2);
    const totalProfitLossPercentage = ((totalProfitLoss / totalInvestment) * 100).toFixed(2);
    return {
      totalInvestment,
      totalValue,
      totalProfitLoss,
      totalProfitLossPercentage,
    };
  };

  const summary = calculateSummary();

  const getProfitLossClass = (value) => {
    return value >= 0 ? 'positive' : 'negative';
  };

  const totalPortfolioValue = portfolio.reduce((acc, item) => acc + parseFloat(item.amount) * parseFloat(item.current_price), 0);

  const pieData = {
    labels: portfolio.map(item => item.token),
    datasets: [{
      data: portfolio.map(item => ((parseFloat(item.amount) * parseFloat(item.current_price)) / totalPortfolioValue * 100).toFixed(2)),
      backgroundColor: portfolio.map((_, index) => `hsl(${index * 360 / portfolio.length}, 70%, 50%)`),
    }]
  };

  const barData = {
    labels: portfolio.map(item => item.token),
    datasets: [
      {
        label: 'Investment',
        data: portfolio.map(item => (parseFloat(item.amount) * parseFloat(item.price)).toFixed(2)),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        barThickness: 20,
      },
      {
        label: 'Current Value',
        data: portfolio.map(item => (parseFloat(item.amount) * parseFloat(item.current_price)).toFixed(2)),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        barThickness: 20,
      }
    ]
  };

  const waterfallData = {
    labels: portfolio.map(item => item.token),
    datasets: [
      {
        label: 'Investment',
        data: portfolio.map(item => (parseFloat(item.amount) * parseFloat(item.price)).toFixed(2)),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
      {
        label: 'Current Value',
        data: portfolio.map(item => (parseFloat(item.amount) * parseFloat(item.current_price)).toFixed(2)),
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
      }
    ]
  };

  const waterfallOptions = {
    indexAxis: 'y', // Make the chart horizontal
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += `$${parseFloat(context.raw).toFixed(2)}`;
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true
      },
      y: {
        stacked: true
      }
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    document.body.classList.toggle('dark-mode', newDarkMode);
    localStorage.setItem('darkMode', newDarkMode);
  };

  const handleMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleRowClick = (index) => {
    const currentIndex = expandedRows.indexOf(index);
    const newExpandedRows = [...expandedRows];

    if (currentIndex === -1) {
      newExpandedRows.push(index);
    } else {
      newExpandedRows.splice(currentIndex, 1);
    }

    setExpandedRows(newExpandedRows);
  };

  const getPerformanceData = (item) => ({
    labels: ['Investment', 'Current Value', 'ATH Value'],
    datasets: [
      {
        label: 'Value ($)',
        data: [
          (parseFloat(item.amount) * parseFloat(item.price)).toFixed(2),
          (parseFloat(item.amount) * parseFloat(item.current_price)).toFixed(2),
          (parseFloat(item.amount) * parseFloat(item.ath_price)).toFixed(2),
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 99, 132, 0.6)',
        ],
      },
    ],
  });

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedToken(null);
    setAmount('');
    setPrice('');
    setToken('');
    setSearchResults([]);
    setError('');
    document.getElementById('add-token').style.display = 'block'; // Show the search bar
  };

  const sortedPortfolio = React.useMemo(() => {
    return [...portfolio].sort((a, b) => {
      const profitA = (parseFloat(a.amount) * parseFloat(a.current_price)) - (parseFloat(a.amount) * parseFloat(a.price));
      const profitB = (parseFloat(b.amount) * parseFloat(b.current_price)) - (parseFloat(b.amount) * parseFloat(b.price));
      return profitB - profitA;
    });
  }, [portfolio]);

  const handleLoadMore = () => {
    setVisibleItems((prevVisibleItems) => prevVisibleItems + 10); // Load 10 more items
  };

  const calculatePortfolioHealth = () => {
    const totalProfitLossPercentage = parseFloat(summary.totalProfitLossPercentage);

    if (totalProfitLossPercentage >= 20) {
        return { label: 'Low Risk', color: 'green' }; // Green pill
    }
    if (totalProfitLossPercentage >= 0) {
        return { label: 'Medium Risk', color: 'orange' }; // Orange pill
    }
    return { label: 'High Risk', color: 'red' }; // Red pill
};


const portfolioHealth = calculatePortfolioHealth();

  return (
    <div style={{ fontFamily: 'var(--font-family)', WebkitFontSmoothing: 'var(--font-smoothing)' }}>
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} hideToggle={portfolio.length === 0} />
      <div className="container" style={{ padding: '5px 20px', paddingBottom: '80px' }}> {/* Add container class with responsive padding and margin-top */}
        <div className="content">
          {portfolio.length > 0 && (
            <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '10px', fontSize: '12px', position: 'relative' }}>
  <span style={{ fontWeight: 'bold' }}></span>
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <span 
      style={{ 
        backgroundColor: portfolioHealth.color, 
        padding: '4px 8px', 
        borderRadius: '40px', 
        color: 'white', 
        fontSize: '12px', 
        cursor: 'pointer',
        display: 'inline-block'
      }}
      onMouseEnter={(e) => e.target.nextSibling.style.display = 'block'}
      onMouseLeave={(e) => e.target.nextSibling.style.display = 'none'}
    >
      {portfolioHealth.label}
    </span>
    <span 
      style={{ 
        display: 'none',
        position: 'absolute',
        left: '0',  /* Aligns tooltip to the left */
        top: '120%', /* Places it below the health rating */
        backgroundColor: 'white',
        color: 'black',
        padding: '6px 10px',
        borderRadius: '5px',
        fontSize: '10px',
        whiteSpace: 'nowrap',
        border: '1px solid #ccc',
        boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
        zIndex: '10'
      }}
    >
      Portfolio Health
    </span>
  </div>
</div>







              <table className="summary-table" style={{ fontSize: '0.9em' }}> {/* Make text smaller */}
                <thead>
                  <tr>
                    <th>Σ Current Value</th>
                    <th>Σ Investment</th>
                    <th>Σ P&L $</th>
                    <th>Σ P&L %</th>
                    <th>5x Profit</th>
                    <th>10x Profit</th>
                    <th>20x Profit</th>
                    <th>100x Profit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${formatPrice(summary.totalValue)}</td>
                    <td>${formatPrice(summary.totalInvestment)}</td>
                    <td className={getProfitLossClass(summary.totalProfitLoss)}>${formatPrice(summary.totalProfitLoss)}</td>
                    <td className={getProfitLossClass(summary.totalProfitLossPercentage)}>{summary.totalProfitLossPercentage}%</td>
                    <td>${formatPrice(summary.totalInvestment * 5)}</td>
                    <td>${formatPrice(summary.totalInvestment * 10)}</td>
                    <td>${formatPrice(summary.totalInvestment * 20)}</td>
                    <td>${formatPrice(summary.totalInvestment * 100)}</td>
                  </tr>
                </tbody>
              </table>
              <section
  className="portfolio-charts"
  style={{
    background: 'var(--bg-color)',
    margin: '20px 0',
    padding: '20px',
    borderRadius: '2px',
    border: '1px solid var(--border-color)',
    fontFamily: 'var(--font-family)'
  }}
>
  {/* Add class for Portfolio Charts section */}
  <h2>Portfolio Charts</h2>
  <div className="charts">
    <div className="chart heatmap">
      <Heatmap data={pieData} view={view} />
    </div>
    <div className="chart">
      <h3>Investment vs. Current Value</h3>
      {portfolio.length > 0 ? (
        <Bar data={waterfallData} options={waterfallOptions} />
      ) : (
        <div className="no-data-container">
          <img src={Rocket} alt="No Data" className="no-data-icon" />
        </div>
      )}
    </div>
  </div>
</section>


            </>
          )}
          {portfolio.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1px', textAlign: 'center' }}>
  <h1>Manage and analyze your cryptocurrency portfolio with ease. </h1>
  <p style={{ maxWidth: '600px', marginBottom: '10px' }}>
    Track investments, visualize performance, and simulate future scenarios.
  </p>
  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
    <button
      className="button action-button add-token-button"
      onClick={() => setIsAddModalOpen(true)}
    >
      Add Token
    </button>
    <button
      className="button action-button what-if-button"
      onClick={() => setIsWhatIfOpen(true)}
    >
      What If
    </button>
  </div>
  <LandingPage />
</div>

)}


 {/* Render LandingPage component when there is no data */}
          {portfolio.length > 0 && ( // Hide the action buttons section when there is no data
          <section>
            <div className="action-buttons">
              <button className="button action-button add-token-button" onClick={() => setIsAddModalOpen(true)}>Add Token</button>
              <button className="button action-button what-if-button" onClick={() => setIsWhatIfOpen(true)}>What If</button>
              
            </div>
          </section>
          )}
          {portfolio.length > 0 && ( // Hide the portfolio table section when there is no data
          <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <h2>Portfolio Table</h2>
  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
    <RefreshPortfolioTable refreshPortfolio={refreshPrices} />
    <IconButton onClick={() => setHideNumbers(!hideNumbers)} style={{ fontSize: '0.8em' }}>
                  {hideNumbers ? <VisibilityOffIcon style={{ color: darkMode ? 'white' : 'black' }} /> : <VisibilityIcon style={{ color: darkMode ? 'white' : 'black' }} />}
                  <span style={{ color: darkMode ? 'white' : 'black' }}>{hideNumbers ? ' Show' : ' Hide'}</span>
    </IconButton>
  </div>
</div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Token</th>
                    <th>Amount</th>
                    <th>Purchase Price</th>
                    <th>Current Price</th>
                    <th>Investment</th>
                    <th>Value</th>
                    <th>Profit/Loss</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPortfolio.slice(0, visibleItems).map((item, index) => {
                    const investment = (parseFloat(item.amount) * parseFloat(item.price)).toFixed(2);
                    const value = (parseFloat(item.amount) * parseFloat(item.current_price)).toFixed(2);
                    const profitLoss = (value - investment).toFixed(2);
                    const profitLossPercentage = ((profitLoss / investment) * 100).toFixed(2);
                    const isRowExpanded = expandedRows.includes(index);
                    const additionalMetrics = calculateAdditionalMetrics(item, btcMarketCap, ethMarketCap);
                    return (
                      <React.Fragment key={generateRowKey(item)}>
                      <tr onClick={() => handleRowClick(index)} style={{ cursor: 'pointer' }}>
  <td>{index + 1}</td>
  <td>
    <img src={item.thumb || ''} alt={item.token} style={{ width: '20px', marginRight: '10px' }} />
    {item.token} ({item.symbol ? item.symbol.toUpperCase() : ''})
  </td>
  <td>{item.amount}</td>
  <td>
    {hideNumbers ? (
      <span className="gray-pill" style={{ width: `${item.price.toString().length}ch` }} />
    ) : (
      formatDetailedPrice(item.price)
    )}
  </td>
  <td>
    {hideNumbers ? (
      <span className="gray-pill" style={{ width: `${item.current_price.toString().length + 2}ch` }} />
    ) : (
      `$${formatDetailedPrice(item.current_price)}`
    )}
  </td>
  <td>
    {hideNumbers ? (
      <span className="gray-pill" style={{ width: `${investment.toString().length + 2}ch` }} />
    ) : (
      `$${formatPrice(investment)}`
    )}
  </td>
  <td>
    {hideNumbers ? (
      <span className="gray-pill" style={{ width: `${value.toString().length + 2}ch` }} />
    ) : (
      `$${formatPrice(value)}`
    )}
  </td>
  <td className={profitLoss >= 0 ? 'positive' : 'negative'}>
  {hideNumbers ? (
    <>
      <span className="gray-pill" style={{ width: `${profitLoss.toString().length + 2}ch` }} />
      <span> ({profitLossPercentage}%)</span>
    </>
  ) : (
    `$${formatPrice(profitLoss)} (${profitLossPercentage}%)`
  )}
</td>

  <td onClick={(e) => e.stopPropagation()}>
    <button onClick={(event) => handleMenuClick(event, index)}>⋮</button>
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl) && menuIndex === index}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => { handleEdit(generateRowKey(item)); handleMenuClose(); }}>Edit</MenuItem>
      <MenuItem onClick={() => { handleDelete(generateRowKey(item)); handleMenuClose(); }}>Delete</MenuItem>
    </Menu>
  </td>
</tr>

                        <tr>
                          <td colSpan="9" style={{ padding: 0, border: 0 }}>
                            <Collapse in={isRowExpanded} timeout={500} unmountOnExit>
                              <Box margin={1}>
                                <div className="expanded-row-content">
                                  <div className="expanded-row-column">
                                    <div className="expanded-row-section">
                                      <p><strong>ATH Price:</strong> {hideNumbers ? '****' : `$${formatDetailedPrice(item.ath_price)}`}</p>
                                      <p><strong>Market Cap:</strong> {hideNumbers ? '****' : `$${formatPrice(item.market_cap)}`}</p>
                                      <p><strong>Total Supply:</strong> {hideNumbers ? '****' : formatPrice(item.total_supply)}</p>
                                      <hr />
                                      <p><strong>Market Cap Tier:</strong> {additionalMetrics.marketCapTier}</p> 
                                      <hr />
                                      <p><strong>Price at BTC Market Cap:</strong> {hideNumbers ? '****' : `$${additionalMetrics.btcComparisonPrice}`}</p>
                                      <p><strong>Price at ETH Market Cap:</strong> {hideNumbers ? '****' : `$${additionalMetrics.ethComparisonPrice}`}</p>
                                    </div>
                                  </div>
                                  <div className="expanded-row-column">
                                    <div className="expanded-row-section">
                                      <p><strong>Close to ATH:</strong> {hideNumbers ? '****' : `${additionalMetrics.closeToAth}%`}</p>
                                      <p><strong>ATH Profit:</strong> {hideNumbers ? '****' : `$${formatPrice(additionalMetrics.athProfit)}`}</p>
                                      <p><strong>5x Profit:</strong> {hideNumbers ? '****' : `$${formatPrice(additionalMetrics.fiveXProfit)}`}</p>
                                      <p><strong>10x Profit:</strong> {hideNumbers ? '****' : `$${formatPrice(additionalMetrics.tenXProfit)}`}</p>
                                      <p><strong>20x Profit:</strong> {hideNumbers ? '****' : `$${formatPrice(additionalMetrics.twentyXProfit)}`}</p>
                                      <p><strong>100x Profit:</strong> {hideNumbers ? '****' : `$${formatPrice(additionalMetrics.hundredXProfit)}`}</p>
   <hr />
                                      <p><strong>Breakeven Multiplier:</strong> {hideNumbers ? '****' : additionalMetrics.breakevenMultiplier}</p>
                                      <p><strong>Analysis:</strong> {additionalMetrics.breakevenMessage}</p>
                                    </div>
                                  </div>
                                  <div className="expanded-row-column">
                                    <div className="expanded-row-section">
                                      <h3>Token Performance Overview</h3>
                                      <div style={{ width: '100%', height: '200px' }}>
                                        <Bar data={getPerformanceData(item)} options={waterfallOptions} />
                                        <br />
                                         <p><strong>Action:</strong> <span style={{ color: additionalMetrics.actionColor }}>{additionalMetrics.action}</span></p> {/* Add action field with color */}
                                      </div>
                                    </div>
                                  </div>
                                  
                                </div>
                              </Box>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {visibleItems < sortedPortfolio.length && (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button className="button action-button" onClick={handleLoadMore}>Load More</button>
                </div>
              )}
            </div>
          </section>
          )}
          {isModalOpen && (
            <Modal
              open={isModalOpen}
              onClose={resetEditState}
              aria-labelledby="edit-token-title"
              aria-describedby="edit-token-description"
            >
              <Box className="modal-box" sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)', 
                bgcolor: 'background.paper', 
                boxShadow: 24, 
                color:'var(--text-color-black)',
                p: 4 
              }}>
                <h2 id="edit-token-title">Edit Token</h2>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <img src={editTokenThumb || ''} alt={editTokenName} style={{ width: '40px', marginRight: '10px' }} /> {/* Add null check */}
                  <h3>{editTokenName}</h3>
                </div>
                <p id="edit-token-description">Edit the details of the token.</p>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label htmlFor="edit-amount">Amount</label>
                  <input
                    id="edit-amount"
                    type="number"
                    placeholder="Amount"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                  />
                  <label htmlFor="edit-price">Purchase Price</label>
                  <input
                    id="edit-price"
                    type="number"
                    placeholder="Purchase Price"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                  <Button onClick={resetEditState} color="secondary">Cancel</Button>
                  <Button onClick={handleUpdate} color="primary">Update</Button>
                </div>
              </Box>
            </Modal>
          )}
          {isWhatIfOpen && (
            <WhatIf
              open={isWhatIfOpen}
              onClose={() => setIsWhatIfOpen(false)}
              portfolio={portfolio}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                boxShadow: 24,
                color: 'var(--light-text-color)',
                p: 4
              }}
            />
          )}
          <Modal
            open={isDeleteModalOpen}
            onClose={cancelDelete}
            aria-labelledby="delete-confirmation-title"
            aria-describedby="delete-confirmation-description"
          >
            <Box className="modal-box" sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)', 
              bgcolor: 'background.paper', 
              boxShadow: 24, 
              color:'var(--text-color-black)',
              p: 4 
            }}>
              <h2 id="delete-confirmation-title">Confirm Delete</h2>
              {deleteIndex !== null && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <img src={portfolio[deleteIndex].thumb || ''} alt={portfolio[deleteIndex].token} style={{ width: '40px', marginRight: '10px' }} />
                  <span>{portfolio[deleteIndex].token}</span>
                </div>
              )}
              <p id="delete-confirmation-description">Are you sure you want to delete this token?</p>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={cancelDelete} color="secondary">Cancel</Button>
                <Button onClick={confirmDelete} color="primary">Confirm</Button>
              </div>
            </Box>
          </Modal>
          <Modal
            open={isAddModalOpen}
            onClose={handleCloseAddModal}
            aria-labelledby="add-token-title"
            aria-describedby="add-token-description"
          >
            <Box className="modal-box" sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)', 
              bgcolor: 'background.paper', 
              boxShadow: 24, 
              color:'var(--text-color-black)',
              p: 4,
              width: '80%', // Adjust the width to make the modal box wider
              maxWidth: '600px', // Set a maximum width for the modal box
            }}>
              <h2 id="add-token-title">Add Token</h2>
              <p id="add-token-description">Enter the details of the token.</p>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label htmlFor="add-token">Token name, symbol or id</label>
                <input
                  id="add-token"
                  type="text"
                  placeholder="Token name, symbol or id"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="large-input" // Add CSS class for styling
                />
                {selectedToken && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <img src={selectedToken.thumb || ''} alt={selectedToken.name} style={{ width: '20px', marginRight: '10px' }} /> {/* Add null check */}
                    <span>{selectedToken.name} ({selectedToken.symbol.toUpperCase()})</span>
                    <IconButton size="small" onClick={handleRemoveSelection} style={{ marginLeft: '10px', fontSize: '0.8em' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                )}
                {error && <p style={{ color: 'red' }}>{error}</p>}
                {loading && <p>Loading...</p>}
                {searchResults.length > 0 && (
                  <ul style={{ maxHeight: '200px', overflowY: 'scroll', listStyle: 'none', padding: 0 }}>
                    {searchResults.map((result) => (
                      <li
                        key={result.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #ddd',
                        }}
                        onClick={() => handleSelect(result)}
                      >
                        <img
                          src={result.thumb || 'https://via.placeholder.com/20'} // Use CoinGecko's `thumb` or a placeholder
                          alt={result.name}
                          style={{ width: '20px', height: '20px', marginRight: '10px' }}
                        />
                        <span>
                          <strong>{result.symbol.toUpperCase()}</strong> - {result.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <label htmlFor="add-amount">Amount</label>
                <input
                  id="add-amount"
                  type="number"
                  placeholder="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="large-input" // Add CSS class for styling
                />
                <label htmlFor="add-price">Purchase Price</label>
                <input
                  id="add-price"
                  type="number"
                  placeholder="Purchase Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="large-input" // Add CSS class for styling
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <Button onClick={handleCloseAddModal} color="secondary">Cancel</Button>
                <Button onClick={handleAddFromForm} color="primary">Add Token</Button>
              </div>
            </Box>
          </Modal>
        </div>
      </div>
      <Footer /> {/* Add the Footer component */}
      
    </div>
  );
};

export default CryptoForm;

